/**
 * @netwatchai/chat — public API.
 *
 * Two ways to embed the widget:
 *
 * 1) **Declarative** — drop the custom element into your HTML:
 *
 *      <netwatch-widget client-id="..." api-endpoint="..."></netwatch-widget>
 *
 * 2) **Programmatic** (one-line PHP / CMS friendly) — call initBubble:
 *
 *      <script type="module">
 *        import NetwatchChat from "https://cdn.jsdelivr.net/npm/@netwatchai/chat@latest/lib/web.min.js";
 *        NetwatchChat.initBubble({
 *          clientId: "9f20750b-…",
 *          apiHost: "https://api.netwatch.ai",
 *        });
 *      </script>
 *
 * The username is auto-resolved from Zabbix's localStorage when the
 * widget is mounted inside a Zabbix page; for other hosts, pass
 * `username` explicitly.
 */

import { NetwatchWidgetElement } from './NetwatchWidget'

export { ChatWidget } from './ChatWidget'
export { NetwatchWidgetElement }

/**
 * Options accepted by {@link initBubble}.
 *
 * Tolerant of the legacy netwatch-chat-js option names so PHP snippets
 * that pass `apiHost`, `placement`, theme.*, etc. work without edits.
 * Options we don't implement yet are accepted and ignored — that lets
 * the host page upgrade the package without touching its embed code.
 */
export interface InitBubbleOptions {
  /** REQUIRED — Netwatch client UUID. */
  clientId: string
  /**
   * API base URL (e.g. https://api.netwatch.ai).
   * Legacy hosts may include `/api/chat`; we strip it.
   */
  apiHost?: string
  /** Alias for `apiHost`. */
  apiEndpoint?: string
  /** Override the auto-resolved user. */
  username?: string
  /** Where to append the element (default: document.body). */
  target?: HTMLElement
  /** Reserved (accepted, not yet honored). */
  theme?: unknown
  /** Reserved. */
  placement?: string
  /** Reserved. */
  brandColor?: string
  /** Reserved. */
  onOpen?: (...args: unknown[]) => unknown
  /** Reserved. */
  onClose?: (...args: unknown[]) => unknown
  /** Reserved. */
  onAnswer?: (...args: unknown[]) => unknown
  /** Unknown/legacy options are accepted and ignored. */
  [key: string]: unknown
}

/**
 * Mount the chat widget on the current page.
 *
 * @returns The mounted <netwatch-widget> element. Call
 *          `.remove()` on it to unmount.
 */
export function initBubble(opts: InitBubbleOptions = {} as InitBubbleOptions): HTMLElement {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('initBubble must run in a browser environment')
  }
  if (!opts.clientId) {
    throw new Error('initBubble: `clientId` is required')
  }

  // Make sure the custom element is registered. Importing
  // NetwatchWidget at the top of this file already triggers
  // customElements.define; this is a defensive check for callers
  // that load the bundle in odd orders.
  if (!customElements.get('netwatch-widget')) {
    void NetwatchWidgetElement
  }

  const el = document.createElement('netwatch-widget')
  el.setAttribute('client-id', String(opts.clientId))

  const apiBase = normalizeApiBase(opts.apiHost ?? opts.apiEndpoint)
  if (apiBase) el.setAttribute('api-endpoint', apiBase)

  if (opts.username) el.setAttribute('username', String(opts.username))

  const target = opts.target instanceof HTMLElement ? opts.target : document.body
  if (target) {
    target.appendChild(el)
  } else {
    // body isn't there yet — wait for it.
    document.addEventListener('DOMContentLoaded', () => {
      document.body.appendChild(el)
    }, { once: true })
  }

  return el
}

/**
 * Strip trailing slashes and the legacy `/api/chat` suffix old embeds
 * use, so callers can pass either `https://api.netwatch.ai` OR
 * `https://api.netwatch.ai/api/chat` and we land at the same base URL.
 */
function normalizeApiBase(input: string | undefined): string {
  if (!input || typeof input !== 'string') return ''
  let base = input.replace(/\/+$/, '')
  base = base.replace(/\/api\/chat$/, '')
  return base
}

// Default export keeps the legacy
//   import NetwatchChat from "..."; NetwatchChat.initBubble({...})
// pattern working as a single-line import.
const NetwatchChat = {
  initBubble,
  NetwatchWidgetElement,
}

export default NetwatchChat
